import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Feature Catalog - Comprehensive Feature Selection Portal
 *
 * 72 features with advanced filtering by category, complexity, tags, and timeline.
 * Professional laymen-friendly descriptions for non-technical stakeholders.
 */
import { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaFilter, FaTimes, FaInfoCircle, FaRocket, FaCubes, FaList } from 'react-icons/fa';
import { PageContainer, PageTitle, Button, Text } from '../../../components/PageStyles';
import { FEATURES, CATEGORIES, COMPLEXITIES, TAGS } from './featureData';
import { apiClient } from '../../../utils/apiClient';
import BundleView from './BundleView';
const FeatureCatalog = () => {
    const navigate = useNavigate();
    const [selectedFeatures, setSelectedFeatures] = useState(new Set());
    const [categoryFilter, setCategoryFilter] = useState('All Categories');
    const [complexityFilter, setComplexityFilter] = useState('All Levels');
    const [selectedTags, setSelectedTags] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [showLaymenMode, setShowLaymenMode] = useState(true);
    const [approvedFeatures, setApprovedFeatures] = useState(new Set());
    const [viewMode, setViewMode] = useState('features');
    // Load approved features from Development Dashboard
    useEffect(() => {
        loadApprovedFeatures();
    }, []);
    const loadApprovedFeatures = async () => {
        try {
            const response = await apiClient.get('feature-development');
            if (response.ok && response.data?.success) {
                const approvedIds = new Set(response.data.data.map((f) => f.featureId));
                setApprovedFeatures(approvedIds);
            }
        }
        catch (error) {
            console.log('Could not load approved features:', error);
        }
    };
    // Filter features
    const filteredFeatures = useMemo(() => {
        return FEATURES.filter(feature => {
            // Category filter
            if (categoryFilter !== 'All Categories' && feature.category !== categoryFilter) {
                return false;
            }
            // Complexity filter
            if (complexityFilter !== 'All Levels') {
                const complexity = complexityFilter.split(' (')[0]; // Extract "Low" from "Low (Quick Win)"
                if (feature.complexity !== complexity) {
                    return false;
                }
            }
            // Tag filter (show if has ANY selected tag)
            if (selectedTags.size > 0) {
                const hasTag = feature.tags.some(tag => selectedTags.has(tag));
                if (!hasTag)
                    return false;
            }
            // Search query
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return (feature.title.toLowerCase().includes(query) ||
                    feature.description.toLowerCase().includes(query) ||
                    feature.laymenDescription.toLowerCase().includes(query) ||
                    feature.tags.some(tag => tag.includes(query)));
            }
            return true;
        });
    }, [categoryFilter, complexityFilter, selectedTags, searchQuery]);
    const handleFeatureSelect = (featureId) => {
        setSelectedFeatures(prev => {
            const newSet = new Set(prev);
            if (newSet.has(featureId)) {
                newSet.delete(featureId);
            }
            else {
                newSet.add(featureId);
            }
            return newSet;
        });
    };
    const handleTagToggle = (tag) => {
        setSelectedTags(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tag)) {
                newSet.delete(tag);
            }
            else {
                newSet.add(tag);
            }
            return newSet;
        });
    };
    const clearAllFilters = () => {
        setCategoryFilter('All Categories');
        setComplexityFilter('All Levels');
        setSelectedTags(new Set());
        setSearchQuery('');
    };
    const handleRequestDevelopment = () => {
        localStorage.setItem('requested-features', JSON.stringify(Array.from(selectedFeatures)));
        navigate('/admin/features?tab=dashboard');
    };
    // Reload approved features when tab becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadApprovedFeatures();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', loadApprovedFeatures);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', loadApprovedFeatures);
        };
    }, []);
    const clearSelection = () => {
        setSelectedFeatures(new Set());
    };
    const handleSelectBundle = (featureIds) => {
        setSelectedFeatures(prev => {
            const newSet = new Set(prev);
            featureIds.forEach(id => newSet.add(id));
            return newSet;
        });
    };
    const activeFiltersCount = (categoryFilter !== 'All Categories' ? 1 : 0) +
        (complexityFilter !== 'All Levels' ? 1 : 0) +
        selectedTags.size;
    return (_jsxs(PageContainer, { children: [_jsxs(Header, { children: [_jsxs(HeaderTop, { children: [_jsxs("div", { children: [_jsx(PageTitle, { children: "Feature Catalog" }), _jsxs(SubtitleText, { children: [viewMode === 'bundles' ? '10 curated bundles' : '106 individual features', " to enhance your platform"] })] }), _jsxs(ViewModeControls, { children: [_jsxs(ViewModeToggle, { children: [_jsxs(ModeButton, { "$active": viewMode === 'bundles', onClick: () => setViewMode('bundles'), children: [_jsx(FaCubes, { style: { marginRight: '6px' } }), "Bundles"] }), _jsxs(ModeButton, { "$active": viewMode === 'features', onClick: () => setViewMode('features'), children: [_jsx(FaList, { style: { marginRight: '6px' } }), "Individual Features"] })] }), viewMode === 'features' && (_jsxs(DescToggle, { children: [_jsx(ToggleLabel, { children: "Descriptions:" }), _jsx(ToggleButton, { "$active": !showLaymenMode, onClick: () => setShowLaymenMode(false), children: "Technical" }), _jsx(ToggleButton, { "$active": showLaymenMode, onClick: () => setShowLaymenMode(true), children: "Simple" })] }))] })] }), _jsxs(HelpText, { children: [_jsx(FaInfoCircle, { style: { marginRight: '8px', flexShrink: 0 } }), viewMode === 'bundles'
                                ? 'Browse curated feature packages designed for specific business needs. Select entire bundles or individual features.'
                                : 'Select individual features. Each includes a simple explanation and real-world comparison. Use filters to narrow down.'] })] }), viewMode === 'bundles' && (_jsx(BundleView, { selectedFeatures: selectedFeatures, onSelectBundle: handleSelectBundle, approvedFeatures: approvedFeatures })), viewMode === 'features' && (_jsxs(_Fragment, { children: [_jsxs(FilterSection, { children: [_jsxs(FilterHeader, { children: [_jsxs(FilterTitle, { children: [_jsx(FaFilter, { style: { marginRight: '8px' } }), "Filters", activeFiltersCount > 0 && (_jsxs(FilterCount, { children: [activeFiltersCount, " active"] }))] }), activeFiltersCount > 0 && (_jsxs(ClearButton, { onClick: clearAllFilters, children: [_jsx(FaTimes, { style: { marginRight: '6px' } }), "Clear All"] }))] }), _jsxs(FilterGrid, { children: [_jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "Search Features:" }), _jsx(SearchInput, { type: "text", placeholder: "Search by name, description, or tag...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "Category:" }), _jsx(Select, { value: categoryFilter, onChange: (e) => setCategoryFilter(e.target.value), children: CATEGORIES.map(cat => (_jsx("option", { value: cat, children: cat }, cat))) })] }), _jsxs(FilterGroup, { children: [_jsx(FilterLabel, { children: "Complexity:" }), _jsx(Select, { value: complexityFilter, onChange: (e) => setComplexityFilter(e.target.value), children: COMPLEXITIES.map(comp => (_jsx("option", { value: comp, children: comp }, comp))) })] })] }), _jsxs(TagSection, { children: [_jsx(FilterLabel, { children: "Filter by Tags:" }), _jsx(TagGrid, { children: TAGS.map(tag => (_jsx(TagChip, { "$active": selectedTags.has(tag), onClick: () => handleTagToggle(tag), children: tag }, tag))) })] }), _jsxs(ResultsSummary, { children: ["Showing ", _jsx("strong", { children: filteredFeatures.length }), " of ", _jsx("strong", { children: FEATURES.length }), " features", selectedFeatures.size > 0 && (_jsxs("span", { style: { marginLeft: '16px', color: 'var(--accent-color)' }, children: ["\u2022 ", _jsx("strong", { children: selectedFeatures.size }), " selected"] }))] })] }), _jsx(FeatureGrid, { children: filteredFeatures.map((feature) => (_jsxs(FeatureCard, { "$color": feature.color, "$selected": selectedFeatures.has(feature.id), onClick: () => handleFeatureSelect(feature.id), children: [_jsxs(CardHeader, { children: [_jsx(FeatureIcon, { "$color": feature.color, children: feature.icon }), _jsxs(HeaderRight, { children: [approvedFeatures.has(feature.id) && (_jsxs(ApprovedBadge, { children: [_jsx(FaRocket, {}), " In Development"] })), _jsx(SelectionIndicator, { "$selected": selectedFeatures.has(feature.id), children: selectedFeatures.has(feature.id) && _jsx(FaCheckCircle, {}) })] })] }), _jsxs(CardContent, { children: [_jsx(FeatureTitle, { children: feature.title }), _jsx(FeatureDescription, { children: showLaymenMode ? feature.laymenDescription : feature.description }), showLaymenMode && (_jsxs(RealWorldComparison, { children: [_jsx(ComparisonIcon, { children: "\uD83D\uDCA1" }), feature.realWorldComparison] })), _jsxs(MetaInfo, { children: [_jsxs(MetaItem, { children: [_jsx(MetaLabel, { children: "Category:" }), _jsx(MetaValue, { children: feature.category })] }), _jsxs(MetaItem, { children: [_jsx(MetaLabel, { children: "Complexity:" }), _jsx(ComplexityBadge, { "$complexity": feature.complexity, children: feature.complexity })] })] }), _jsxs(ValueProposition, { children: [_jsx(ValueLabel, { children: "Why This Matters:" }), _jsx(ValueText, { children: feature.value })] }), _jsxs(TagList, { children: [feature.tags.slice(0, 4).map(tag => (_jsx(Tag, { children: tag }, tag))), feature.tags.length > 4 && (_jsxs(Tag, { children: ["+", feature.tags.length - 4] }))] })] }), _jsx(CardFooter, { children: _jsx(SelectButton, { "$selected": selectedFeatures.has(feature.id), "$color": feature.color, children: selectedFeatures.has(feature.id) ? (_jsxs(_Fragment, { children: [_jsx(FaCheckCircle, { style: { marginRight: '8px' } }), "Selected"] })) : ('Select This Feature') }) })] }, feature.id))) }), filteredFeatures.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FaFilter, {}) }), _jsx(EmptyText, { children: "No features match your filters" }), _jsx(Button, { variant: "secondary", onClick: clearAllFilters, children: "Clear Filters" })] }))] })), selectedFeatures.size > 0 && (_jsxs(SelectionSummary, { children: [_jsxs(SummaryTitle, { children: [selectedFeatures.size, " Feature", selectedFeatures.size > 1 ? 's' : '', " Selected"] }), _jsx(SummaryText, { children: "Ready to request development for your selected features?" }), _jsxs(SelectedPreview, { children: [Array.from(selectedFeatures).slice(0, 5).map(id => {
                                const feature = FEATURES.find(f => f.id === id);
                                return feature ? (_jsx(SelectedChip, { "$color": feature.color, children: feature.title }, id)) : null;
                            }), selectedFeatures.size > 5 && (_jsxs(SelectedChip, { "$color": "#666", children: ["+", selectedFeatures.size - 5, " more"] }))] }), _jsxs(SummaryActions, { children: [_jsxs(Button, { onClick: handleRequestDevelopment, children: ["Request Development (", selectedFeatures.size, ")"] }), _jsx(Button, { variant: "secondary", onClick: clearSelection, children: "Clear Selection" })] })] }))] }));
};
// Theme breakpoints
const breakpoints = {
    small: '375px',
    mobile: '768px',
    tablet: '1024px',
};
// Styled Components
const Header = styled.div `
  margin-bottom: 32px;
`;
const HeaderTop = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 24px;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;
const SubtitleText = styled(Text) `
  margin-top: 8px;
  margin-bottom: 0;
`;
const ViewModeControls = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;

  @media (max-width: ${breakpoints.mobile}) {
    align-items: stretch;
  }
`;
const ViewModeToggle = styled.div `
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  padding: 4px;
  border-radius: 4px;
`;
const ModeButton = styled.button `
  padding: 10px 20px;
  background: ${props => props.$active ? 'var(--accent-color)' : 'transparent'};
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 2px;

  &:hover {
    color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-primary)'};
  }
`;
const DescToggle = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ToggleLabel = styled.span `
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const ToggleButton = styled.button `
  padding: 8px 16px;
  background: ${props => props.$active ? 'var(--accent-color)' : 'transparent'};
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  border: 1px solid var(--border-color);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: var(--accent-color);
  }
`;
const HelpText = styled.div `
  display: flex;
  align-items: flex-start;
  padding: 16px;
  background: var(--bg-secondary);
  border-left: 3px solid var(--accent-color);
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin-bottom: 24px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 13px;
  }
`;
const FilterSection = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 32px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
  }
`;
const FilterHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const FilterTitle = styled.h3 `
  font-family: 'Adamina', serif;
  font-size: 18px;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const FilterCount = styled.span `
  margin-left: 12px;
  padding: 4px 10px;
  background: var(--accent-color);
  color: var(--bg-primary);
  font-size: 11px;
  font-weight: 700;
  border-radius: 12px;
`;
const ClearButton = styled.button `
  padding: 8px 16px;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s;

  &:hover {
    border-color: var(--accent-color);
    color: var(--text-primary);
  }
`;
const FilterGrid = styled.div `
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
const FilterGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FilterLabel = styled.label `
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const SearchInput = styled.input `
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;
const Select = styled.select `
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  option {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;
const TagSection = styled.div `
  margin-bottom: 20px;
`;
const TagGrid = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;
const TagChip = styled.button `
  padding: 6px 12px;
  background: ${props => props.$active ? 'var(--accent-color)' : 'var(--bg-tertiary)'};
  color: ${props => props.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-color)' : 'var(--border-color)'};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: capitalize;

  &:hover {
    border-color: var(--accent-color);
    transform: translateY(-2px);
  }
`;
const ResultsSummary = styled.div `
  font-size: 14px;
  color: var(--text-secondary);
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
  font-weight: 600;

  strong {
    color: var(--text-primary);
  }
`;
const FeatureGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 100px;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;
const FeatureCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid ${props => props.$selected ? props.$color : 'var(--border-color)'};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.$color};
    opacity: ${props => props.$selected ? 1 : 0};
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: ${props => props.$color};
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px 16px;
  }
`;
const CardHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const HeaderRight = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;
const ApprovedBadge = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #22c55e22, #22c55e44);
  border: 1px solid #22c55e;
  color: #22c55e;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
`;
const FeatureIcon = styled.div `
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => `linear-gradient(135deg, ${props.$color}22, ${props.$color}44)`};
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;
const SelectionIndicator = styled.div `
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$selected ? 'var(--accent-color)' : 'var(--bg-tertiary)'};
  border: 2px solid ${props => props.$selected ? 'var(--accent-color)' : 'var(--border-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${props => props.$selected ? 'var(--bg-primary)' : 'transparent'};
  transition: all 0.3s;
`;
const CardContent = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const FeatureTitle = styled.h3 `
  font-family: 'Adamina', serif;
  font-size: 20px;
  font-weight: 400;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`;
const FeatureDescription = styled.p `
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
`;
const RealWorldComparison = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: var(--bg-tertiary);
  border-left: 3px solid var(--accent-color);
  font-size: 13px;
  color: var(--text-tertiary);
  font-style: italic;
  line-height: 1.5;
`;
const ComparisonIcon = styled.span `
  font-size: 16px;
  flex-shrink: 0;
`;
const MetaInfo = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const MetaItem = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const MetaLabel = styled.span `
  font-size: 10px;
  font-weight: 700;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;
const MetaValue = styled.span `
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;
const ComplexityBadge = styled.span `
  font-size: 13px;
  font-weight: 600;
  color: ${props => {
    switch (props.$complexity) {
        case 'Low': return '#22c55e';
        case 'Medium': return '#3b82f6';
        case 'High': return '#f59e0b';
        case 'Very High': return '#ef4444';
        default: return 'var(--text-primary)';
    }
}};
`;
const ValueProposition = styled.div `
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
`;
const ValueLabel = styled.div `
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
`;
const ValueText = styled.div `
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.5;
`;
const TagList = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Tag = styled.span `
  padding: 4px 8px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-tertiary);
  font-size: 10px;
  font-weight: 600;
  text-transform: lowercase;
`;
const CardFooter = styled.div `
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;
const SelectButton = styled.button `
  width: 100%;
  padding: 12px 20px;
  background: ${props => props.$selected ? props.$color : 'transparent'};
  color: ${props => props.$selected ? '#fff' : 'var(--text-primary)'};
  border: 2px solid ${props => props.$selected ? props.$color : 'var(--border-color)'};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$color};
    color: #fff;
    border-color: ${props => props.$color};
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 80px 24px;
  color: var(--text-tertiary);
`;
const EmptyIcon = styled.div `
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;
const EmptyText = styled.div `
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 24px;
  color: var(--text-secondary);
`;
const SelectionSummary = styled.div `
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 700px;
  width: calc(100% - 48px);
  background: var(--bg-secondary);
  border: 2px solid var(--accent-color);
  padding: 24px;
  text-align: center;
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);

  @media (max-width: ${breakpoints.mobile}) {
    bottom: 8px;
    width: calc(100% - 16px);
    padding: 12px;
    max-height: 30vh;
    overflow-y: auto;
  }
`;
const SummaryTitle = styled.h3 `
  font-family: 'Adamina', serif;
  font-size: 20px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
    letter-spacing: 1px;
    margin-bottom: 4px;
  }
`;
const SummaryText = styled.p `
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`;
const SelectedPreview = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
  max-height: 100px;
  overflow-y: auto;

  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 12px;
    max-height: 60px;
    gap: 4px;
  }
`;
const SelectedChip = styled.div `
  padding: 6px 12px;
  background: ${props => `${props.$color}22`};
  border: 1px solid ${props => props.$color};
  color: ${props => props.$color};
  font-size: 11px;
  font-weight: 600;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 10px;
    padding: 4px 8px;
  }
`;
const SummaryActions = styled.div `
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: ${breakpoints.mobile}) {
    gap: 8px;

    button {
      flex: 1;
      padding: 10px 16px;
      font-size: 12px;
    }
  }
`;
export default FeatureCatalog;
